import { Paper, Tab, Tabs } from "@material-ui/core";
import axios from "axios";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { BsArrowLeftShort } from "react-icons/bs";
import {
    FaAngleDoubleLeft,
    FaAngleDoubleRight,
    FaAngleLeft,
    FaAngleRight,
    FaMinusSquare,
    FaPlusSquare,
    FaSortDown,
    FaSortUp,
} from "react-icons/fa";
import {
    useExpanded,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from "react-table";
import { useDomain } from "../../../contexts/DomainContext";
import { Loader } from "../Loader";
import { FETCH_COUNSUMER_LIST, NODE_API_URL } from "./../../../env_config";
import { CartDetails } from "./CartDetails";
import { MockOrderHistoryColumn, MockShortListColumn } from "./Columns";
import { ExpandedDetails } from "./ExpandedDetails";
import { useSelector } from "react-redux";
import { OrderHistorySorting } from "./OrderHistorySorting";
import { OrderHistoryFilter } from "./OrderHistoryFilter";

export const ShortListTable = (props) => {
    const { origin } = useDomain();
    const { hidePrice, hideCertificateNumber } = useSelector((state) => state.CcModeSettingReducer);
    const [isauth, setAuth] = useState(false);
    const [loader, setLoader] = useState(true);
    const [authmessage, setAuthMessage] = useState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedValidatePassword = useCallback(
        debounce((body) => {
            axios({
                method: "POST",
                url: `${NODE_API_URL}/ccmode/validate-password`,
                headers: {
                    "Access-Control-Allow-Origin": "*",
                    "Content-Type": "application/json",
                },
                data: body,
            })
                .then(function(res) {
                    if (res && res.data.success) {
                        setAuth(true);
                    } else {
                        setAuth(false);
                        setAuthMessage(
                            res.data?.msg ||
                                props.language.CCMODE_CART_INVALID_CREDENTIALS,
                        );
                    }
                })
                .catch((err) => {
                    setAuthMessage(
                        props.language.CCMODE_CART_SOMETHING_WENT_WRONG,
                    );
                });
        }, 400),
        [],
    );

    useEffect(() => {
        // Cleanup: cancel debounce on unmount or before next call.
        return () => {
            debouncedValidatePassword.cancel();
        };
    }, [debouncedValidatePassword]);

    const ValidatePassword = (password) => {
        const values = {
            client: origin,
            mail: props.user.mail,
            password: password,
        };

        debouncedValidatePassword(values);
    };
    const [items, setItems] = useState([]);
    const [orderHistory, setOrderHistory] = useState([]);
    const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
    const [orderPagination, setOrderPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 0,
    });
    const [tab, setTab] = useState("cart");
    const [sorting, setSorting] = useState("created_desc");
    const [listName, setListName] = useState("");
    const [consumerName, setConsumerName] = useState("");
    const [orderConsumers, setOrderConsumers] = useState([]);

    const listNames = useMemo(
        () => [...new Set((orderConsumers || []).map((x) => x.ListName).filter(Boolean))],
        [orderConsumers],
    );

    const consumerNames = useMemo(
        () => [...new Set((orderConsumers || []).map((x) => x.ConsumerName).filter(Boolean))],
        [orderConsumers],
    );

    const renderRowSubComponent = React.useCallback(
        ({ row }, type) => (
            <ExpandedDetails
                data={row.original}
                skeletonLength={row.original.ShortListedStones || undefined}
                language={props.language}
                themeData={props.themeData}
                type={type}
            />
        ),
        [props.language, props.themeData],
    );

    useEffect(() => {
        const timer = setTimeout(() => setLoader(false), 500);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!isauth) return;

        const cancelSource = axios.CancelToken.source();
        const customerId = props?.user?.CustomerId;
        const userId = props?.user?.userId;

        const headers = {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        };

        const getShortListData = async () => {
            try {
                axios({
                    method: "GET",
                    url: `${FETCH_COUNSUMER_LIST}/${customerId}?userId=${userId}&removeBuyRequest=true&onlyShortListedStones=true`,
                    headers,
                    cancelToken: cancelSource.token,
                })
                    .then((res) => {
                        if (res.data.message === "UnAuthorized") {
                            localStorage.clear();
                            window.location.reload(true);
                            return;
                        }

                        const data = res.data || [];
                        const temp = data.filter(
                            (ele) =>
                                ele.ConsumerId ===
                                localStorage.getItem("C_id"),
                        );

                        setItems(temp.length > 0 ? temp : data);
                    })
                    .catch(() => {});
            } catch (err) {
                if (!axios.isCancel(err)) {
                    console.error("Cart data fetch failed:", err);
                }
            }
        };

        getShortListData();

        return () => {
            cancelSource.cancel("Component unmounted — requests cancelled");
        };
    }, [isauth, props.user?.CustomerId, props.user?.userId]);

    const cartColumns = useMemo(
        () => MockShortListColumn(props.language, props.themeData),
        [props.language, props.themeData],
    );

    const orderHistoryColumns = useMemo(
        () =>
            MockOrderHistoryColumn(
                props.language,
                props.themeData,
                hideCertificateNumber,
                hidePrice,
            ),
        [
            props.language,
            props.themeData,
            hideCertificateNumber,
            hidePrice,
        ],
    );

    const columns = tab === "cart" ? cartColumns : orderHistoryColumns;

    const data = useMemo(() => items, [items]);
    const orderHistoryData = useMemo(() => orderHistory, [orderHistory]);

    const {
        getTableProps,
        getTableBodyProps,
        prepareRow,
        headerGroups,
        page,
        nextPage,
        previousPage,
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        pageCount,
        visibleColumns,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data: tab === "cart" ? data : orderHistoryData,
            manualPagination: tab === "order-history",
            autoResetPage: false,
            pageCount:
                tab === "order-history"
                    ? orderPagination.totalPages
                    : undefined,
            initialState: {
                hiddenColumns: ["ConsumerId"],
                pageSize: 10,
            },
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => {
                return [
                    {
                        Header: () => null,
                        id: "expander",
                        Cell: ({ row }) => (
                            <span {...row.getToggleRowExpandedProps()}>
                                {row.isExpanded ? (
                                    <FaMinusSquare
                                        className="text-success"
                                        title={props.language.CCMODE_CART_COLLAPSE}
                                    />
                                ) : (
                                    <FaPlusSquare
                                        className="text-primary"
                                        title={props.language.CCMODE_CART_EXPAND}
                                    />
                                )}
                            </span>
                        ),
                    },
                    ...columns,
                ];
            });
        },
    );

    useEffect(() => {
        if (!isauth || tab !== "order-history") return;

        const cancelSource = axios.CancelToken.source();
        const customerId = props?.user?.CustomerId;
        const userId = props?.user?.userId;

        const headers = { "Access-Control-Allow-Origin": "*", "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("accessToken")}`};

        const fetchOrderHistory = async () => {
            setOrderHistoryLoading(true);

            try {
                const [consumerRes, diamondRes] = await Promise.all([
                    axios.get(
                        `${FETCH_COUNSUMER_LIST}/${customerId}?userId=${userId}&purchaseHistoryWithBuyRequest=true&onlyShortListedStones=true`,
                        {
                            headers,
                            cancelToken: cancelSource.token,
                        },
                    ),
                    axios.post(
                        `${NODE_API_URL}/CustomerShortListdata?purchaseHistoryWithBuyRequest=true&onlyShortListedStones=true&paginate=true&page=${pageIndex + 1}&pageSize=${pageSize}&sorting=${sorting}&listName=${encodeURIComponent(listName)}&consumerName=${encodeURIComponent(consumerName)}`,
                        {
                            customerId,
                            userId,
                        },
                        {
                            headers,
                            cancelToken: cancelSource.token,
                        },
                    ),
                ]);

                if (cancelSource.token.reason) return;

                if (
                    consumerRes.data?.message === "UnAuthorized" ||
                    diamondRes.data?.message === "UnAuthorized"
                ) {
                    localStorage.clear();
                    window.location.reload(true);
                    return;
                }

                setOrderConsumers(consumerRes.data || []);

                const consumerMap = new Map(
                    (consumerRes.data || []).map((item) => [
                        String(item.ConsumerId),
                        item,
                    ]),
                );

                const diamonds = diamondRes.data?.Result || [];

                setOrderHistory(
                    diamonds.map((diamond) => {
                        const consumer = consumerMap.get(
                            String(diamond.ConsumerId),
                        );

                        return {
                            ...diamond,
                            ListName: consumer?.ListName || "",
                            ConsumerName: consumer?.ConsumerName || "",
                            EMail: consumer?.EMail || "",
                            ContactNo: consumer?.ContactNo || "",
                            Notes: consumer?.Notes || "",
                            ShortListedStones:
                                consumer?.ShortListedStones ?? 0,
                            TotalCarats:
                                consumer?.TotalCarats ?? 0,
                            ListCreatedAt: consumer?.CreatedAt || "",
                        };
                    }),
                );

                setOrderPagination(
                    diamondRes.data?.pagination || {
                        page: pageIndex + 1,
                        pageSize,
                        total: 0,
                        totalPages: 0,
                    },
                );
            } catch (err) {
                if (!axios.isCancel(err)) {
                    console.error("Order history fetch failed:", err);
                }
            } finally {
                if (!cancelSource.token.reason) {
                    setOrderHistoryLoading(false);
                }
            }
        };

        fetchOrderHistory();

        return () => {
            cancelSource.cancel("Order history request cancelled");
        };
    }, [
        isauth,
        tab,
        pageIndex,
        pageSize,
        sorting,
        listName,
        consumerName,
        props.user?.CustomerId,
        props.user?.userId,
    ]);

    const handleListName = (value) => {
        setListName(value);
        gotoPage(0);
    };

    const handleConsumerName = (value) => {
        setConsumerName(value);
        gotoPage(0);
    };

    const handleSorting = (value) => {
        setSorting(value);
        gotoPage(0);
    };

    function handleTabChange(event, newValue) {
        window.glob = newValue;
        setTab(newValue);
        gotoPage(0);
    }

    if (props.user === undefined) {
        return;
    }

    return loader ? (
        <Loader />
    ) : (
        <>
            {localStorage.getItem("C_id") &&
            localStorage.getItem("C_id") !== "-1" &&
            localStorage.getItem("C_id") !== "-2" ? (
                <CartDetails
                    ConsumerId={{ ConsumerId: localStorage.getItem("C_id") }}
                    language={props.language}
                />
            ) : (
                <div>
                    {isauth ? (
                        <div>
                            <div className="card card-custom card-sticky gutter-b">
                                <div className="card-header mb-3 align-content-center">
                                    <div className="card-title d-block">
                                        <span className="card-label font-weight-bolder text-dark">
                                            {
                                                props.language
                                                    .CCMODE_SHORTLISTPAGE_SHORTLISTHISTORY
                                            }
                                        </span>
                                        <br />
                                        <div
                                            className="cursor-pointer small text-hover-primary"
                                            onClick={() =>
                                                window.history.go(-1)
                                            }
                                        >
                                            <BsArrowLeftShort />{" "}
                                            {
                                                props.language
                                                    .CCMODE_CART_GO_TO_SEARCH_RESULTS
                                            }
                                        </div>
                                    </div>
                                    <Paper position="static">
                                        <Tabs
                                            value={tab}
                                            onChange={handleTabChange}
                                        >
                                            <Tab
                                                label={
                                                    props.language
                                                        .CCMODE_CART_CART
                                                }
                                                value="cart"
                                            />
                                            <Tab
                                                label={
                                                    props.language
                                                        .CCMODE_CART_ORDER_HISTORY
                                                }
                                                value="order-history"
                                            />
                                        </Tabs>
                                    </Paper>
                                </div>
                                {tab === "cart" ? (
                                    <>
                                        <div className="card-body pt-7 pb-0 mt-n3">
                                            <div className="table-responsive w-100">
                                                <table
                                                    className="table table-bordered table-hover"
                                                    {...getTableProps()}
                                                >
                                                    <thead className="custom_datatable_head">
                                                        {headerGroups.map(
                                                            (headerGroup) => (
                                                                <tr
                                                                    {...headerGroup.getHeaderGroupProps()}
                                                                >
                                                                    {headerGroup.headers.map(
                                                                        (
                                                                            column,
                                                                        ) => (
                                                                            <th
                                                                                className="text-center align-middle"
                                                                                {...column.getHeaderProps(
                                                                                    column.getSortByToggleProps(),
                                                                                )}
                                                                            >
                                                                                {column.render(
                                                                                    "Header",
                                                                                )}
                                                                                <span className="text-secondary">
                                                                                    {column.isSorted ? (
                                                                                        column.isSortedDesc ? (
                                                                                            <FaSortDown />
                                                                                        ) : (
                                                                                            <FaSortUp />
                                                                                        )
                                                                                    ) : (
                                                                                        ""
                                                                                    )}
                                                                                </span>
                                                                            </th>
                                                                        ),
                                                                    )}
                                                                </tr>
                                                            ),
                                                        )}
                                                    </thead>
                                                    <tbody
                                                        {...getTableBodyProps()}
                                                    >
                                                        {items.length !== 0 ? (
                                                            page.map(
                                                                (row, i) => {
                                                                    prepareRow(
                                                                        row,
                                                                    );
                                                                    return (
                                                                        <React.Fragment
                                                                            key={
                                                                                i
                                                                            }
                                                                        >
                                                                            <tr
                                                                                {...row.getRowProps()}
                                                                                key={
                                                                                    row.id
                                                                                }
                                                                            >
                                                                                {row.cells.map(
                                                                                    (
                                                                                        cell,
                                                                                        i,
                                                                                    ) => {
                                                                                        return (
                                                                                            <td
                                                                                                className="text-center align-middle"
                                                                                                {...cell.getCellProps(
                                                                                                    {
                                                                                                        className:
                                                                                                            cell
                                                                                                                .column
                                                                                                                .className,
                                                                                                    },
                                                                                                )}
                                                                                                key={
                                                                                                    i
                                                                                                }
                                                                                            >
                                                                                                {cell.render(
                                                                                                    "Cell",
                                                                                                )}
                                                                                            </td>
                                                                                        );
                                                                                    },
                                                                                )}
                                                                            </tr>
                                                                            {row.isExpanded ? (
                                                                                <tr className="expanded_tr">
                                                                                    <td
                                                                                        className="p-0"
                                                                                        colSpan={
                                                                                            visibleColumns.length
                                                                                        }
                                                                                    >
                                                                                        {renderRowSubComponent(
                                                                                            {
                                                                                                row,
                                                                                            },
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ) : null}
                                                                        </React.Fragment>
                                                                    );
                                                                },
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    className="text-center align-middle"
                                                                    colSpan="100%"
                                                                >
                                                                    {
                                                                        props
                                                                            .language
                                                                            .CCMODE_CART_NO_DATA_FOUND
                                                                    }
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className="card-footer border-0">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className="table_page_length">
                                                    {
                                                        props.language
                                                            .CCMODE_THEME3_SEARCHDIAMOND_PAGE
                                                    }{" "}
                                                    {pageIndex + 1} of{" "}
                                                    {tab === "order-history"
                                                        ? orderPagination.totalPages
                                                        : pageOptions.length}
                                                </span>
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            gotoPage(0)
                                                        }
                                                        disabled={
                                                            !canPreviousPage
                                                        }
                                                    >
                                                        <FaAngleDoubleLeft />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            previousPage()
                                                        }
                                                        disabled={
                                                            !canPreviousPage
                                                        }
                                                    >
                                                        <FaAngleLeft />
                                                    </button>
                                                    <span className="font-weight-bold text-muted">
                                                        {"  "}
                                                        <input
                                                            className="form-control d-inline-block form-control-sm"
                                                            type="number"
                                                            value={
                                                                pageIndex + 1
                                                            }
                                                            // defaultValue={pageIndex + 1}
                                                            onChange={(e) => {
                                                                const pageNumber = e
                                                                    .target
                                                                    .value
                                                                    ? Number(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      ) - 1
                                                                    : 0;
                                                                gotoPage(
                                                                    pageNumber,
                                                                );
                                                            }}
                                                            style={{
                                                                maxWidth:
                                                                    "70px",
                                                            }}
                                                        />
                                                        {"  "}of{"  "}{" "}
                                                        {pageOptions.length}
                                                        {"  "}
                                                    </span>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            nextPage()
                                                        }
                                                        disabled={!canNextPage}
                                                    >
                                                        <FaAngleRight />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-light"
                                                        onClick={() =>
                                                            gotoPage(
                                                                pageCount - 1,
                                                            )
                                                        }
                                                        disabled={!canNextPage}
                                                    >
                                                        <FaAngleDoubleRight />
                                                    </button>
                                                </div>
                                                <span className="d-flex align-items-center">
                                                    <span className="text-nowrap page_size">
                                                        {
                                                            props.language
                                                                .CCMODE_THEME3_SEARCHDIAMOND_ROWSPERPAGE
                                                        }
                                                        :{" "}
                                                    </span>
                                                    <select
                                                        className="form-select ml-2 custom-select py-1"
                                                        value={pageSize}
                                                        onChange={(e) =>
                                                            setPageSize(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        {[10, 25, 50].map(
                                                            (pageSize) => (
                                                                <option
                                                                    key={
                                                                        pageSize
                                                                    }
                                                                    value={
                                                                        pageSize
                                                                    }
                                                                >
                                                                    {pageSize}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="card-body pt-7 pb-0 mt-n3">
                                            <div className="d-flex mb-2">
                                                <OrderHistorySorting
                                                    language={props.language}
                                                    sorting={sorting}
                                                    setSorting={handleSorting}
                                                />
                                                <OrderHistoryFilter
                                                    language={props.language}
                                                    listName={listName}
                                                    consumerName={consumerName}
                                                    listNames={listNames}
                                                    consumerNames={consumerNames}
                                                    setListName={handleListName}
                                                    setConsumerName={handleConsumerName}
                                                />
                                            </div>
                                            <div className="table-responsive w-100">
                                                <table
                                                    className="table table-bordered table-hover"
                                                    {...getTableProps()}
                                                >
                                                    <thead className="custom_datatable_head">
                                                        {headerGroups.map(
                                                            (headerGroup) => (
                                                                <tr
                                                                    {...headerGroup.getHeaderGroupProps()}
                                                                >
                                                                    {headerGroup.headers.map(
                                                                        (
                                                                            column,
                                                                        ) => (
                                                                            <th
                                                                                className="text-center align-middle"
                                                                                {...column.getHeaderProps(
                                                                                    column.getSortByToggleProps(),
                                                                                )}
                                                                            >
                                                                                {column.render(
                                                                                    "Header",
                                                                                )}
                                                                                <span className="text-secondary">
                                                                                    {column.isSorted ? (
                                                                                        column.isSortedDesc ? (
                                                                                            <FaSortDown />
                                                                                        ) : (
                                                                                            <FaSortUp />
                                                                                        )
                                                                                    ) : (
                                                                                        ""
                                                                                    )}
                                                                                </span>
                                                                            </th>
                                                                        ),
                                                                    )}
                                                                </tr>
                                                            ),
                                                        )}
                                                    </thead>
                                                    <tbody {...getTableBodyProps()}>
                                                        {orderHistoryLoading ? (
                                                            <tr>
                                                                <td
                                                                    colSpan={visibleColumns.length}
                                                                    className="text-center align-middle"
                                                                >
                                                                    <Loader />
                                                                </td>
                                                            </tr>
                                                        ) : orderHistory.length !== 0 ? (
                                                            page.map((row) => {
                                                                prepareRow(row);

                                                                return (
                                                                    <React.Fragment key={row.id}>
                                                                        <tr {...row.getRowProps()}>
                                                                            {row.cells.map((cell, i) => (
                                                                                <td
                                                                                    className="text-center align-middle"
                                                                                    {...cell.getCellProps({
                                                                                        className:
                                                                                            cell.column.className,
                                                                                    })}
                                                                                    key={i}
                                                                                >
                                                                                    {cell.render("Cell")}
                                                                                </td>
                                                                            ))}
                                                                        </tr>

                                                                        {row.isExpanded ? (
                                                                            <tr className="expanded_tr">
                                                                                <td
                                                                                    className="p-0"
                                                                                    colSpan={visibleColumns.length}
                                                                                >
                                                                                    {renderRowSubComponent(
                                                                                        { row },
                                                                                        "order-history",
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ) : null}
                                                                    </React.Fragment>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    className="text-center align-middle"
                                                                    colSpan={visibleColumns.length}
                                                                >
                                                                    {props.language.CCMODE_CART_NO_DATA_FOUND}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                        <div className="card-footer border-0">
                                            <div className="d-flex align-items-center justify-content-between">
                                                <span className="table_page_length">
                                                    {
                                                        props.language
                                                            .CCMODE_THEME3_SEARCHDIAMOND_PAGE
                                                    }{" "}
                                                    {pageIndex + 1} of{" "}
                                                    {orderPagination.totalPages}{" "}
                                                </span>
                                                <div>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            gotoPage(0)
                                                        }
                                                        disabled={
                                                            !canPreviousPage
                                                        }
                                                    >
                                                        <FaAngleDoubleLeft />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            previousPage()
                                                        }
                                                        disabled={
                                                            !canPreviousPage
                                                        }
                                                    >
                                                        <FaAngleLeft />
                                                    </button>
                                                    <span className="font-weight-bold text-muted">
                                                        {"  "}
                                                        <input
                                                            className="form-control d-inline-block form-control-sm"
                                                            type="number"
                                                            value={
                                                                pageIndex + 1
                                                            }
                                                            onChange={(e) => {
                                                                const pageNumber = e
                                                                    .target
                                                                    .value
                                                                    ? Number(
                                                                          e
                                                                              .target
                                                                              .value,
                                                                      ) - 1
                                                                    : 0;
                                                                gotoPage(
                                                                    pageNumber,
                                                                );
                                                            }}
                                                            style={{
                                                                maxWidth:
                                                                    "70px",
                                                            }}
                                                        />
                                                        {"  "}of{"  "}{" "}
                                                        {orderPagination.totalPages}
                                                        {"  "}
                                                    </span>
                                                    <button
                                                        className="btn btn-sm btn-light mr-2"
                                                        onClick={() =>
                                                            nextPage()
                                                        }
                                                        disabled={!canNextPage}
                                                    >
                                                        <FaAngleRight />
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-light"
                                                        onClick={() =>
                                                            gotoPage(
                                                                pageCount - 1,
                                                            )
                                                        }
                                                        disabled={!canNextPage}
                                                    >
                                                        <FaAngleDoubleRight />
                                                    </button>
                                                </div>
                                                <span className="d-flex align-items-center">
                                                    <span className="text-nowrap page_size">
                                                        {
                                                            props.language
                                                                .CCMODE_THEME3_SEARCHDIAMOND_ROWSPERPAGE
                                                        }
                                                        :{" "}
                                                    </span>
                                                    <select
                                                        className="form-select ml-2 custom-select py-1"
                                                        value={pageSize}
                                                        onChange={(e) =>
                                                            setPageSize(
                                                                Number(
                                                                    e.target
                                                                        .value,
                                                                ),
                                                            )
                                                        }
                                                    >
                                                        {[10, 25, 50].map(
                                                            (pageSize) => (
                                                                <option
                                                                    key={
                                                                        pageSize
                                                                    }
                                                                    value={
                                                                        pageSize
                                                                    }
                                                                >
                                                                    {pageSize}
                                                                </option>
                                                            ),
                                                        )}
                                                    </select>
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    ) : (
                        <Modal
                            show={!isauth}
                            onHide={false}
                            backdrop="static"
                            keyboard={false}
                            className="natTab"
                        >
                            <Modal.Header>
                                <Modal.Title>
                                    {
                                        props.language
                                            .CCMODE_SHORTLISTPAGE_PASSWORD
                                    }
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="row">
                                    <div className="col-12">
                                        <label htmlFor="">
                                            {
                                                props.language
                                                    .CCMODE_SHORTLISTPAGE_PASSWORD1
                                            }
                                            :
                                        </label>
                                        <div className="form-group fv-plugins-icon-container">
                                            <input
                                                placeholder={
                                                    props.language
                                                        .CCMODE_SHORTLISTPAGE_PASSWORD1
                                                }
                                                type="password"
                                                className={`form-control form-control-lg`}
                                                name="Password"
                                                autoComplete="off"
                                                onChange={(e) =>
                                                    ValidatePassword(
                                                        e.target.value,
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                {authmessage ? (
                                    <span className="text-danger">
                                        {authmessage}
                                    </span>
                                ) : (
                                    ""
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <button
                                    className="btn-light-primary btn btn-sm"
                                    onClick={() => window.history.back()}
                                >
                                    {props.language.CCMODE_COMMON_CLOSE}
                                </button>
                            </Modal.Footer>
                        </Modal>
                    )}
                </div>
            )}
        </>
    );
};
